import { useEffect } from 'react'
import { useLocation } from 'react-router'
import mixpanelBrowser from 'mixpanel-browser/src/loaders/loader-module-core'
import type { BuildInfo } from 'common/api/v1/types'
import { IS_PROD, MIXPANEL_TOKEN } from './env'
import type { EnrichedUserWithSettings } from './api/nm-types'

const SOURCE = 'network-manager'
const EVENT = {
  clickComponent: 'Clicked Component',
  clickLink: 'Clicked Link',
  searchedResource: 'Searched Resource',
  viewedComponent: 'Viewed Component',
  viewedPage: 'Viewed Page',
} as const

const init = () => {
  if (!MIXPANEL_TOKEN) return

  // https://github.com/mixpanel/mixpanel-js/blob/master/doc/readme.io/javascript-full-api-reference.md#mixpanelset_config
  mixpanelBrowser.init(MIXPANEL_TOKEN, {
    debug: !IS_PROD,
    persistence: 'localStorage',
    ip: false,
  })
}

const addUserInfo = (user: Pick<EnrichedUserWithSettings, 'role'>) => {
  if (!MIXPANEL_TOKEN) return

  mixpanelBrowser.register({
    role: user.role,
  })
}

const addBuildInfo = (buildInfo: Pick<BuildInfo, 'product' | 'release' | 'buildTime'>) => {
  if (!MIXPANEL_TOKEN) return

  mixpanelBrowser.register({
    build_info_product: buildInfo.product,
    build_info_release: buildInfo.release,
    build_info_build_time: buildInfo.buildTime,
  })
}

const trackEvent = (...args: Parameters<typeof mixpanelBrowser.track>) => {
  if (!MIXPANEL_TOKEN) return
  mixpanelBrowser.track(...args)
}

const trackClickComponent = (props: Record<string, unknown> & { component: string }) => {
  trackEvent(EVENT.clickComponent, props)
}

const trackClickLink = (props: { component: string; to: string }) => {
  trackEvent(EVENT.clickLink, props)
}

const trackSearchResource = (props: { component: string; search_keys: string[] }) => {
  trackEvent(EVENT.searchedResource, props)
}

const reset = () => {
  try {
    mixpanelBrowser.reset()
  } catch (_) {
    // noop
  }
}
export const mixpanel = {
  init,
  addUserInfo,
  addBuildInfo,
  trackClickComponent,
  trackClickLink,
  trackSearchResource,
  reset,
}

export function useComponentTracking(props: Record<string, unknown> & { component: string }) {
  useEffect(() => {
    if (!MIXPANEL_TOKEN) return

    mixpanelBrowser.time_event(EVENT.viewedComponent)
    return () => mixpanelBrowser.track(EVENT.viewedComponent, props)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

export function usePageTracking() {
  const location = useLocation()

  useEffect(() => {
    if (!MIXPANEL_TOKEN) return

    mixpanelBrowser.time_event(EVENT.viewedPage)
    return () => mixpanelBrowser.track(EVENT.viewedPage, { source: SOURCE, path: location.pathname })
  }, [location.pathname])
}
